import { Capability, finance, financeError, financeKeys, newKey } from "@/services/finance";
import { NumberInput, NumberInputProps, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment-jalaali";
import { createContext, useContext, useRef, useState } from "react";
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import DatePicker, { DateObject } from "react-multi-date-picker";

// Amounts stay in rial, as everywhere in the panel and as the API sends them.
export const toNumber = (rial?: string | null) => (rial ? Number(rial) : 0);
export const rial = (value?: string | number | null) => `${Number(value ?? 0).toLocaleString("fa-IR")} ریال`;
export const faNum = (value: number) => value.toLocaleString("fa-IR");

// Business dates travel as YYYY-MM-DD.
export const isoToday = () => moment().format("YYYY-MM-DD");
export const monthStart = () => moment().startOf("jMonth").format("YYYY-MM-DD");
export const faDate = (iso?: string | null) => (iso ? moment(iso.slice(0, 10), "YYYY-MM-DD").format("jYYYY/jMM/jDD") : "—");

const ERRORS: Record<string, string> = {
  STALE_PREVIEW: "این اطلاعات تغییر کرده؛ دوباره بررسی کنید.",
  DAY_CLOSED: "این روز بسته شده است.",
  INSUFFICIENT_STOCK: "تعداد واردشده از موجودی بیشتر است.",
};

// The panel's interceptor already shows the server message; these codes get the clearer product wording.
export function explain(error: unknown) {
  const e = financeError(error);
  if (e.code && ERRORS[e.code]) notifications.show({ color: "red", message: ERRORS[e.code] });
  return e;
}

// One Idempotency-Key per form: a retry of the same action reuses it, and a successful save starts a new one.
export function useFinanceWrite<B, R>(action: (body: B, key: string) => Promise<R>, options?: { success?: string; onSuccess?: (result: R) => void }) {
  const key = useRef(newKey());
  const [pending, setPending] = useState(false);
  const client = useQueryClient();
  const run = async (body: B): Promise<R | undefined> => {
    if (pending) return undefined;
    setPending(true);
    try {
      const result = await action(body, key.current);
      key.current = newKey();
      await client.invalidateQueries(financeKeys.all);
      if (options?.success) notifications.show({ color: "green", message: options.success });
      options?.onSuccess?.(result);
      return result;
    } catch (error) {
      explain(error);
      return undefined;
    } finally {
      setPending(false);
    }
  };
  return { run, pending };
}

type Range = { from: string; to: string };
type FinanceState = { range: Range; setRange: (r: Range) => void; can: (c: Capability) => boolean; ready: boolean };
export const FinanceContext = createContext<FinanceState>({ range: { from: monthStart(), to: isoToday() }, setRange: () => undefined, can: () => false, ready: false });
export const useFinance = () => useContext(FinanceContext);

export const useAccounts = () => useQuery({ queryKey: [...financeKeys.all, "accounts"], queryFn: finance.accounts });
export const useTerminals = (enabled = true) => useQuery({ queryKey: [...financeKeys.all, "terminals"], queryFn: finance.terminals, enabled });
export const useCategories = () => useQuery({ queryKey: [...financeKeys.all, "categories"], queryFn: finance.categories, staleTime: 600_000 });

export const accountOptions = (accounts?: { id: number; name: string; kind: string }[], kinds: string[] = ["Cash", "Bank"]) =>
  (accounts ?? []).filter((a) => kinds.includes(a.kind)).map((a) => ({ value: String(a.id), label: `${a.name} · ${a.kind === "Cash" ? "صندوق" : "بانک"}` }));

export function MoneyInput(props: Omit<NumberInputProps, "value" | "onChange"> & { value: string; onChange: (rial: string) => void }) {
  const { value, onChange, ...rest } = props;
  return (
    <NumberInput
      thousandSeparator=","
      allowDecimal={false}
      allowNegative={false}
      hideControls
      rightSection={<span style={{ fontSize: 12, color: "var(--mantine-color-dimmed)", paddingLeft: 8 }}>ریال</span>}
      rightSectionWidth={44}
      styles={{ input: { direction: "ltr", textAlign: "left" } }}
      value={value === "" ? "" : Number(value)}
      onChange={(v) => onChange(v === "" || v === undefined ? "" : String(Math.trunc(Number(v))))}
      {...rest}
    />
  );
}

export function DateField({ label, value, onChange, max, error, required }: { label: string; value: string; onChange: (iso: string) => void; max?: string; error?: string; required?: boolean }) {
  return (
    <DatePicker
      calendar={persian}
      locale={persianFa}
      value={value ? new DateObject(new Date(`${value}T12:00:00`)).convert(persian, persianFa) : ""}
      maxDate={max ? new Date(`${max}T12:00:00`) : undefined}
      onChange={(d: DateObject | null) => onChange(d ? moment(d.toDate()).format("YYYY-MM-DD") : "")}
      containerStyle={{ width: "100%" }}
      render={(shown: string, open: () => void) => <TextInput label={label} withAsterisk={required} value={shown} onClick={open} onFocus={open} readOnly error={error} placeholder="انتخاب تاریخ" />}
    />
  );
}
